/**
 * `<Icon>` — renders an inline SVG from the active icon registry (or a directly
 * supplied `svg` / `src`). Weave: 18px, 1.4 hairline stroke, `currentColor` (so an
 * icon takes its parent's text color). Lean DOM — a single host `<span>` whose
 * innerHTML is the SVG (Weave has no `{@html}`, so we set it via a ref + effect).
 *
 * Accessibility: pass `label` for a meaningful icon (`role="img"` + `aria-label`);
 * with no label the icon is decorative (`aria-hidden`), the WAI-ARIA default.
 *
 * Reactive: changing `name` (or the registry's async cache) re-renders in place.
 *
 *   import Icon, { configureIcons } from '@weave-framework/ui/icon';
 *   <Icon name={{ 'search' }} />
 *   <Icon name={{ 'trash-2' }} label={{ 'Delete' }} />
 */

import { signal, effect, type Signal } from '@weave-framework/runtime';
import { activeIcons, type IconRegistry } from './icons.js';

// Re-export the registry surface so `@weave-framework/ui/icon` is the one import.
export {
  configureIcons,
  createIconRegistry,
  activeIcons,
  inlineIcons,
  spriteIcons,
  IconContext,
} from './icons.js';
export type { IconRegistry, IconConfig, IconSource } from './icons.js';

export interface IconProps {
  /** Name to look up in the active registry (built-in Lucide set by default). */
  name?: string;
  /** A complete `<svg>…</svg>` string to render directly (bypasses the registry). */
  svg?: string;
  /** URL of a standalone SVG file to fetch and render (bypasses the registry). */
  src?: string;
  /** Accessible label. Present → `role="img"`; absent → decorative (`aria-hidden`). */
  label?: string;
  /**
   * Extra class on the host, alongside `weave-icon` — the per-instance styling hook 30 of the
   * library's other components already take. An icon is the one you most often need to size or
   * colour differently from its neighbours, and it was the one that did not accept it.
   */
  class?: string;
}

const FORBIDDEN_SVG_TAGS: Set<string> = new Set(['script', 'foreignobject']);

/** Attributes whose value a browser resolves as a URL. */
const URL_ATTRS: Set<string> = new Set(['href', 'xlink:href', 'src']);

/** SMIL elements that can WRITE another element's attribute after this scrubber has finished. */
const ANIMATION_TAGS: Set<string> = new Set(['animate', 'set', 'animatetransform', 'animatemotion']);

/**
 * Is this URL safe to leave on an element?
 *
 * An allow-list, not a `javascript:` test. Browsers strip tabs, newlines and C0 controls from a URL
 * BEFORE reading its scheme, so `java<TAB>script:alert(1)` runs while matching no pattern that spells
 * the scheme out — it got through the earlier check, confirmed in a browser. Deny-lists lose that game
 * repeatedly; naming what is allowed ends it.
 */
function safeUrl(value: string): boolean {
  // Same normalisation the browser does before resolving the scheme.
  const url: string = value.replace(/[\x00- ]/g, '');
  const scheme: RegExpExecArray | null = /^([a-z][a-z0-9+.-]*):/i.exec(url);
  if (!scheme) return true; // relative, or a `#fragment` — no scheme to abuse
  return ['http', 'https', 'mailto', 'tel'].includes(scheme[1].toLowerCase());
}

/** Recursively strip active content: `on*` handlers, unsafe URLs, script and animation elements. */
function scrubSvg(el: Element): void {
  for (const attr of [...el.attributes]) {
    const name: string = attr.name.toLowerCase();
    if (name.startsWith('on')) {
      el.removeAttribute(attr.name);
    } else if (URL_ATTRS.has(name) && !safeUrl(attr.value)) {
      el.removeAttribute(attr.name);
    }
  }
  for (const child of [...el.children]) {
    const tag: string = child.tagName.toLowerCase();
    if (FORBIDDEN_SVG_TAGS.has(tag)) {
      child.remove();
      continue;
    }
    // An animation whose target is a URL attribute writes that attribute after this pass is over, so
    // scrubbing its current value proves nothing — the value it will install is the one that matters.
    // Animations that move or transform are left alone; only the ones that can set a URL are refused.
    if (ANIMATION_TAGS.has(tag) && URL_ATTRS.has((child.getAttribute('attributeName') ?? '').toLowerCase())) {
      child.remove();
      continue;
    }
    scrubSvg(child);
  }
}

/**
 * Sanitize an SVG string before it goes into `innerHTML` (zero-dep, native `DOMParser`). Parsed as
 * `image/svg+xml`, so nothing executes during parsing; `<script>`/`<foreignObject>`, every `on*`
 * event-handler attribute, and `javascript:` URLs are removed. Returns '' for non-SVG / malformed
 * input. Guards the `svg`/`src` inputs (a `<svg onload=…>` would otherwise run on insertion).
 */
export function sanitizeSvg(markup: string): string {
  if (!markup) return '';
  // Headless SSR/SSG has no `DOMParser`, so we cannot safely scrub here. Fail closed — emit nothing rather
  // than un-sanitized markup (which could carry `on*`/`javascript:` and would run when the browser parses the
  // server HTML, before the client takes over). The client re-renders the icon (CSR) and sanitizes it then.
  if (typeof DOMParser === 'undefined') return '';
  const doc: Document = new DOMParser().parseFromString(markup, 'image/svg+xml');
  const root: Element | null = doc.documentElement;
  if (!root || root.nodeName.toLowerCase() === 'parsererror' || doc.querySelector('parsererror')) return '';
  scrubSvg(root);
  return root.outerHTML;
}

                                                                                       

export function setup(props: IconProps): { host: Signal<Element | null>; iconClass: () => string } {
  const host: Signal<Element | null> = signal<Element | null>(null);
  const registry: IconRegistry = activeIcons();

  // Render the SVG + apply a11y. Re-runs when `host`, `name`, `svg`, or the
  // registry's async cache changes.
  effect(() => {
    const el: HTMLElement | null = host() as HTMLElement | null;
    if (!el) return;
    if (!props.src) {
      const markup: string | undefined =
        props.svg ?? (props.name ? registry.resolve(props.name) : undefined);
      el.innerHTML = sanitizeSvg(markup ?? '');
    }
    if (props.label) {
      el.setAttribute('role', 'img');
      el.setAttribute('aria-label', props.label);
      el.removeAttribute('aria-hidden');
    } else {
      el.setAttribute('aria-hidden', 'true');
      el.removeAttribute('role');
      el.removeAttribute('aria-label');
    }
  });

  // `src`: fetch a standalone SVG file, reactively (a new src cancels the last).
  effect(() => {
    const url: string | undefined = props.src;
    const el: HTMLElement | null = host() as HTMLElement | null;
    if (!url || !el) return;
    let alive: boolean = true;
    void fetch(url)
      .then((r) => r.text())
      .then((txt) => {
        if (alive) el.innerHTML = sanitizeSvg(txt); // remote SVG is untrusted — sanitize
      });
    return () => {
      alive = false;
    };
  });

  return {
    host,
    iconClass: (): string => (props.class ? `weave-icon ${props.class}` : 'weave-icon'),
  };
}

import { defineComponent } from "@weave-framework/runtime/dom";

import { bindAttr, clone, setRef, template } from "@weave-framework/runtime/dom";

const _t0 = template("<span data-w-9yobjp><\/span>");
function render(ctx, slots) {
  const _r = clone(_t0);
  bindAttr(_r, "class", () => (ctx.iconClass()));
  setRef(ctx.host, _r);
  return _r;
}

export default defineComponent(render, setup);